import { ContractSuretyType } from "@inrev/common";
import {
	useAdminFetchAccountBondingLines,
	useAdminFetchAccountSpecialtyCodes,
	useAdminFetchLatestSuretyAccountUnderwriting,
} from "@portal/api/account/admin";
import { useAdminFetchBond } from "@portal/api/bond/admin";
import { useAdminFetchLatestSuretyQuoteUnderwriting } from "@portal/api/bond-request/admin";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { useQuoteFollowers } from "@portal/api/followers";
import { UpdateQuoteUnderwriting } from "@portal/components/admin/UpdateQuoteUnderwriting";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { cn } from "@portal/components/lib/utils";
import { Button } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { type Tab, TabBar } from "@portal/components/ui/TabBar";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import {
	type ReportScrolledFromTop,
	useScrolledFromTopGestures,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import {
	type FC,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	type WheelEvent,
} from "react";
import type { NonEmptyArray } from "../../../../shared/types";
import { hasMissingRequiredForApprovalFields } from "../../../../utils/schema-fields";
import type { AccountIdentity } from "../../../shared/account/AccountHeader";
import { BondRequestTitle } from "../../../shared/request/BondRequestTitle";
import { AdminWorkflowLayout } from "../../layout/AdminWorkflowLayout";
import { AdminBondRequestCardHeader } from "../../shared/AdminBondRequestCardHeader";
import { AdminChangeBondRequestTermsModal } from "../../shared/AdminChangeBondRequestTermsModal";
import { bondFormUnavailableMessage, isBondFormAvailable } from "../../shared/bondFormAvailability";
import {
	getContractUpdateSchema,
	mapAdminBondRequestToContractUpdateForm,
} from "../../shared/mapAdminBondRequestToContractUpdateForm";
import { AdminTools } from "../../shared/tools/AdminTools";
import { AdminBondRequestHeaderNotices } from "./AdminBondRequestHeaderNotices";
import { AdminBondRequestStickyPanel } from "./AdminBondRequestStickyPanel";
import { AdminBondRequestBondFormTab } from "./tabs/AdminBondRequestBondFormTab";
import { AdminBondRequestOverviewTab } from "./tabs/AdminBondRequestOverviewTab";
import { AdminBondRequestPelPmlTab } from "./tabs/AdminBondRequestPelPmlTab";
import { type BondFormQueryState, type BondRequestTabProps, canChangeTerms } from "./types";
import { type BondRequestTabId, useBondRequestTabNavigation } from "./useBondRequestTabNavigation";

export type AdminBondRequestTabbedLayoutProps = {
	request: AdminBondRequest;
	bondFormQuery: BondFormQueryState;
	account: AccountIdentity;
};

const ENTITY_VIEW_GUTTER_CLASS = "mx-24";

const TABS: NonEmptyArray<Tab<BondRequestTabProps> & { id: BondRequestTabId }> = [
	{ id: "overview", label: "Overview", Component: AdminBondRequestOverviewTab },
	{ id: "bond-form", label: "Bond Form", Component: AdminBondRequestBondFormTab },
	{ id: "pel-pml", label: "PEL/PML", Component: AdminBondRequestPelPmlTab },
];

export const AdminBondRequestTabbedLayout: FC<AdminBondRequestTabbedLayoutProps> = ({
	request,
	bondFormQuery,
	account,
}) => {
	const { bondingLines } = useAdminFetchAccountBondingLines(request.suretyAccountId);
	const accountRate = bondingLines?.bondingLine?.rate;
	const {
		latestUnderwriting,
		latestUnderwritingError,
		latestUnderwritingIsFetching,
		latestUnderwritingIsLoading,
		refetchLatestUnderwriting,
	} = useAdminFetchLatestSuretyAccountUnderwriting(request.suretyAccountId);
	const { latestUnderwriting: quoteUnderwriting } = useAdminFetchLatestSuretyQuoteUnderwriting(
		request.suretyAccountId,
		request.id,
	);
	const {
		rows: specialtyRows,
		isLoading: accountNaicsCodesIsLoading,
		isFetching: accountNaicsCodesFetching,
		error: accountNaicsCodesError,
		refetch: refetchAccountNaicsCodes,
	} = useAdminFetchAccountSpecialtyCodes(request.suretyAccountId);
	const accountNaicsCodes = specialtyRows.map((row) => row.id);
	const [isChangeTermsModalOpen, setIsChangeTermsModalOpen] = useState(false);
	const changeTermsTooltipId = useId();
	const followersControl = useQuoteFollowers(request.suretyAccountId, request.id, true);
	const { activeTabId, setActiveTabId, headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } =
		useBondRequestTabNavigation();
	const contractSuretyType = request.contractSuretyType;
	const bondForm = bondFormQuery.data;
	const bondFormAvailable = isBondFormAvailable(bondForm, bondFormQuery.error);
	const changeTermsUnavailableMessage = bondFormUnavailableMessage(
		bondFormQuery.isFetching,
		bondFormQuery.error,
		"changing terms",
	);

	useEffect(() => {
		if (!bondFormAvailable && isChangeTermsModalOpen) {
			setIsChangeTermsModalOpen(false);
		}
	}, [bondFormAvailable, isChangeTermsModalOpen]);

	const isBidToFinal = request.contractSuretyType === ContractSuretyType.bid_to_final;
	const { bond: bidBond } = useAdminFetchBond(
		request.contractSuretyType === ContractSuretyType.bid_to_final ? request.bidBondId : "",
		isBidToFinal,
	);

	const accountApproved =
		latestUnderwriting?.status === "approved" || request.status === "approved";
	const canApprove = accountApproved && !latestUnderwritingError;

	const isComplete = useMemo(() => {
		const updateForm = mapAdminBondRequestToContractUpdateForm(request);
		const updateSchema = getContractUpdateSchema(request.contractSuretyType);
		// bondAmount is hard-required by the schema rather than requiredForApproval, and an
		// unpriced request stores 0 rather than an empty value, so it needs its own check.
		return request.bondAmount > 0 && !hasMissingRequiredForApprovalFields(updateSchema, updateForm);
	}, [request]);

	const { Component: TabContent } = TABS.find((tab) => tab.id === activeTabId) ?? TABS[0];

	const scrollRef = useRef<HTMLDivElement | null>(null);

	const reportScrolledFromTop = useCallback<ReportScrolledFromTop>(
		(scrolledFromTop) => setHeaderCollapsed(scrolledFromTop),
		[setHeaderCollapsed],
	);

	const {
		onScroll: handlePageScroll,
		attachNativeWheel: attachPageWheel,
		decideWheel: decideChromeWheel,
	} = useScrolledFromTopGestures(reportScrolledFromTop, undefined, getHeaderCollapsed);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollRef.current = node;
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current = node ? attachPageWheel(node) : undefined;
		},
		[attachPageWheel],
	);

	const handleChromeWheel = (e: WheelEvent<HTMLDivElement>) => {
		const target = scrollRef.current;
		if (!target || (e.target instanceof Node && target.contains(e.target))) return;
		const decision = decideChromeWheel(target, e);
		if (decision.collapse) {
			reportScrolledFromTop(true);
			return;
		}
		if (decision.block) return;
		if (decision.expand) reportScrolledFromTop(false);
		// deltaMode 1 means the delta is in lines, not pixels (Firefox)
		target.scrollBy({ top: e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY });
	};

	const handleTabChange = useCallback(
		(tabId: string) => {
			setActiveTabId(tabId);
			scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
		},
		[setActiveTabId],
	);

	if (accountNaicsCodesError) {
		return (
			<ReportedRetryableLoadError
				error={accountNaicsCodesError}
				resource="account specialty"
				onRetry={() => refetchAccountNaicsCodes()}
				retrying={accountNaicsCodesFetching}
				testId="bond-request-tabbed-specialty-load-error"
			/>
		);
	}

	if (latestUnderwritingIsLoading || accountNaicsCodesIsLoading) {
		return <CenteredSpinner />;
	}

	return (
		<AdminWorkflowLayout
			title={
				<BondRequestTitle
					request={{
						suretyAccountId: request.suretyAccountId,
						principal: { name: request.principal.name },
						contractSuretyType,
					}}
					bidBond={bidBond}
				/>
			}
			toolbar={
				<AdminTools
					relation={{ relationType: "quote", relationId: request.id }}
					defaultUnderwriterId={request.underwriterUserId}
				/>
			}
			subHeader={
				<UpdateQuoteUnderwriting
					accountId={request.suretyAccountId}
					quoteId={request.id}
					underwriting={quoteUnderwriting}
				/>
			}
			contentClassName="bg-surface-light"
		>
			<div
				className="w-full h-full flex flex-col min-h-0"
				onWheel={handleChromeWheel}
				data-testid="admin-bond-request-entity-view-layout"
			>
				<div
					data-testid="admin-bond-request-header-wrapper"
					className={cn(
						"shrink-0 grid bg-surface-base transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none",
						headerCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
					)}
				>
					<div className="overflow-hidden min-h-0">
						<div
							className={cn(
								ENTITY_VIEW_GUTTER_CLASS,
								"flex flex-col space-y-[16px] pt-[18px] pb-[10px]",
							)}
						>
							<AdminBondRequestCardHeader
								request={request}
								bondForm={bondFormAvailable ? bondForm : undefined}
								bondFormIsFetching={bondFormQuery.isFetching}
								bondFormError={bondFormQuery.error}
								accountRate={accountRate}
								accountDisplayName={account.displayName}
								actionButtons={
									canChangeTerms(request) ? (
										<>
											<span
												className={cn(
													"w-full",
													!bondFormAvailable && "cursor-not-allowed [&_button]:pointer-events-none",
												)}
												{...(!bondFormAvailable
													? {
															tabIndex: 0,
															"aria-label": changeTermsUnavailableMessage,
															"data-tooltip-id": changeTermsTooltipId,
															"data-tooltip-content": changeTermsUnavailableMessage,
															"data-testid": "change-terms-unavailable",
														}
													: {})}
											>
												<Button
													variant="outline"
													color="secondary"
													className="w-full"
													disabled={!bondFormAvailable}
													onClick={() => setIsChangeTermsModalOpen(true)}
													trackingId="bond-request-admin-change-terms"
												>
													Change Terms
												</Button>
											</span>
											{!bondFormAvailable && (
												<PortaledTooltip id={changeTermsTooltipId} style={{ maxWidth: 260 }} />
											)}
										</>
									) : undefined
								}
								followersControl={followersControl}
								isComplete={isComplete}
								canApprove={canApprove}
								className="pt-[6px] pb-0"
							/>
							<AdminBondRequestHeaderNotices
								request={request}
								bondForm={bondFormAvailable ? bondForm : undefined}
								accountApproved={accountApproved}
								isComplete={isComplete}
								underwriting={{
									error: latestUnderwritingError,
									onRetry: refetchLatestUnderwriting,
									retrying: latestUnderwritingIsFetching,
								}}
							/>
						</div>
					</div>
				</div>

				<div className="relative bg-surface-base flex shrink-0 z-[25] border-b border-b-bc-subtle shadow-md shadow-gray-50">
					<div className={cn(ENTITY_VIEW_GUTTER_CLASS, "flex")}>
						<TabBar
							options={TABS}
							activeTabId={activeTabId}
							onTabChange={handleTabChange}
							className="pt-4 gap-8"
							minimal
							trackingPrefix="bond-request-admin-tab"
						/>
					</div>
					{headerCollapsed && (
						<div className="absolute right-4 top-1/2 -translate-y-1/2 hidden md:block">
							<AdminBondRequestStickyPanel
								request={request}
								followers={followersControl.followers}
							/>
						</div>
					)}
				</div>

				<div
					data-testid="admin-bond-request-scroll-container"
					ref={setScrollRef}
					className="flex-1 min-h-0 overflow-auto overscroll-y-contain"
					onScroll={handlePageScroll}
				>
					<div className={cn(ENTITY_VIEW_GUTTER_CLASS, "flex flex-col mt-6 pb-[125px]")}>
						<TabContent
							request={request}
							bondFormQuery={bondFormQuery}
							accountRate={accountRate}
							accountDisplayName={account.displayName}
							accountNaicsCodes={accountNaicsCodes}
							followersControl={followersControl}
							bidBond={bidBond}
							underwriting={quoteUnderwriting}
						/>
					</div>
				</div>

				{isChangeTermsModalOpen && canChangeTerms(request) && bondFormAvailable && (
					<AdminChangeBondRequestTermsModal
						request={request}
						bondForm={bondForm}
						accountRate={accountRate}
						onClose={() => setIsChangeTermsModalOpen(false)}
					/>
				)}
			</div>
		</AdminWorkflowLayout>
	);
};
